/**
 * Centralized Route Configuration
 *
 * Defines all application routes including authentication and recipe management.
 * Authenticated pages are wrapped in AppLayout (header + nav).
 * Auth pages (login / register / onboarding) render without the layout.
 */

import { Routes, Route } from 'react-router-dom';
import { Login } from '../components/auth/Login';
import { Register } from '../components/auth/Register';
import { Onboarding } from '../components/auth/Onboarding';
import { RecipeList } from '../components/recipes/RecipeList';
import { RecipeForm } from '../components/recipes/RecipeForm';
import { PdfImport } from '../components/recipes/PdfImport';
import { RecipeDetail } from '../components/recipes/RecipeDetail';
import { CookingMode } from '../components/cooking/CookingMode';
import { WeeklyCalendar } from '../components/mealPlan/WeeklyCalendar';
import { ShoppingList } from '../components/shopping/ShoppingList';
import { ReminderSettings } from '../components/settings/ReminderSettings';
import { ComponentDemo } from '../pages/ComponentDemo';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../components/common/AppLayout';

// Home component
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

function Home() {
  const { t } = useTranslation('common');
  const { isAuthenticated, user } = useAuthStore();

  return (
    <div className="welcome">
      <h2>{t('welcome')}</h2>
      <p>{t('welcomeSubtitle')}</p>

      {isAuthenticated ? (
        <div>
          <p>{t('loggedInAs', { email: user?.email })}</p>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/recipes">
              <button>{t('viewRecipes')}</button>
            </Link>
            <Link to="/meal-plan">
              <button>{t('mealPlan')}</button>
            </Link>
            <Link to="/shopping-list">
              <button>{t('shoppingList')}</button>
            </Link>
            <Link to="/settings">
              <button>{t('settings')}</button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="auth-links">
          <Link to="/login">
            <button>{t('login')}</button>
          </Link>
          <Link to="/register">
            <button>{t('register')}</button>
          </Link>
        </div>
      )}
    </div>
  );
}

// Wrapper component for RecipeDetail to handle route params
function RecipeDetailPage() {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const recipeId = parseInt(id || '0', 10);

  if (!recipeId || isNaN(recipeId)) {
    return (
      <div className="recipe-container">
        <div className="error-message">{t('invalidRecipeId')}</div>
        <Link to="/recipes" className="btn-secondary" style={{ marginTop: '1rem' }}>
          {t('backToRecipes')}
        </Link>
      </div>
    );
  }

  return <RecipeDetail recipeId={recipeId} />;
}

// Wrapper component for RecipeForm to handle edit mode
function RecipeEditPage() {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const recipeId = parseInt(id || '0', 10);

  if (!recipeId || isNaN(recipeId)) {
    return (
      <div className="recipe-container">
        <div className="error-message">{t('invalidRecipeId')}</div>
        <Link to="/recipes" className="btn-secondary" style={{ marginTop: '1rem' }}>
          {t('backToRecipes')}
        </Link>
      </div>
    );
  }

  return <RecipeForm recipeId={recipeId} />;
}

// Wrapper component for CookingMode to handle route params
function CookingModePage() {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const recipeId = parseInt(id || '0', 10);

  if (!recipeId || isNaN(recipeId)) {
    return (
      <div className="recipe-container">
        <div className="error-message">{t('invalidRecipeId')}</div>
        <Link to="/recipes" className="btn-secondary" style={{ marginTop: '1rem' }}>
          {t('backToRecipes')}
        </Link>
      </div>
    );
  }

  return <CookingMode recipeId={recipeId} />;
}

/**
 * AppRoutes Component
 *
 * Defines all application routes with React Router.
 * Auth pages render without AppLayout; all other pages use AppLayout.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Authentication Routes — no persistent header/nav */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<Onboarding />} />

      {/* All other routes wrapped in AppLayout */}
      <Route
        path="/"
        element={
          <AppLayout>
            <Home />
          </AppLayout>
        }
      />
      <Route
        path="/recipes"
        element={
          <AppLayout>
            <RecipeList />
          </AppLayout>
        }
      />
      <Route
        path="/recipes/import-pdf"
        element={
          <AppLayout>
            <PdfImport />
          </AppLayout>
        }
      />
      <Route
        path="/recipes/new"
        element={
          <AppLayout>
            <RecipeForm />
          </AppLayout>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <AppLayout>
            <RecipeDetailPage />
          </AppLayout>
        }
      />
      <Route
        path="/recipes/:id/edit"
        element={
          <AppLayout>
            <RecipeEditPage />
          </AppLayout>
        }
      />
      <Route
        path="/recipes/:id/cook"
        element={
          <AppLayout>
            <CookingModePage />
          </AppLayout>
        }
      />
      <Route
        path="/meal-plan"
        element={
          <AppLayout>
            <WeeklyCalendar />
          </AppLayout>
        }
      />
      <Route
        path="/shopping-list"
        element={
          <AppLayout>
            <ShoppingList />
          </AppLayout>
        }
      />
      <Route
        path="/settings"
        element={
          <AppLayout>
            <ReminderSettings />
          </AppLayout>
        }
      />
      <Route
        path="/component-demo"
        element={
          <AppLayout>
            <ComponentDemo />
          </AppLayout>
        }
      />
    </Routes>
  );
}
